import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import { message, Popconfirm, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { EditOutlined } from "@ant-design/icons";
import { DeleteIcon } from "assets/icons";
import { generatePath, Link } from "react-router-dom";
import { ROUTE_PATHS } from "const/paths";
import useHasPermissions from "hooks/useHasPermissions";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import { Permission } from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { pageToSkip } from "utils/tableUtils";
import {
  VENUE_USER_LIST_QUERY,
  useVenueUserDeleteMutation,
  useVenueUserListQuery,
} from "@ticketingplatform/api/dist/venueUser";
import { VenueUser } from "@ticketingplatform/api/dist/venueUser/fragments/__generated__/venueUser";
import { useVenueContext } from "contexts/VenueContext";
import { VenueUserListVariables } from "@ticketingplatform/api/dist/venueUser/__generated__/queries";

import { UseVenueUserListReturnType } from "./types";

export const useVenueUserActionColumns = (): ColumnsType<VenueUser> => {
  const intl = useIntl();
  const canDelete = useHasPermissions([Permission.VENUE_USER_DELETE]);
  const canEdit = useHasPermissions([Permission.VENUE_USER_UPDATE]);
  const [deleteVenueUser, { loading }] = useVenueUserDeleteMutation({
    evictQueries: [VENUE_USER_LIST_QUERY],
    onCompleted: () =>
      message.success(
        intl.formatMessage({
          defaultMessage: "User has been removed!",
        })
      ),
    onError: () =>
      message.error(
        intl.formatMessage({
          defaultMessage: "Cannot remove this User",
        })
      ),
  });

  return useMemo(
    () =>
      canEdit || canDelete
        ? [
            {
              title: intl.formatMessage({
                defaultMessage: "Actions",
              }),
              key: "action",
              render: (_, { id }) => (
                <Space size="middle">
                  {canEdit && (
                    <Link
                      to={generatePath(ROUTE_PATHS.SettingsVenueUserEdit, {
                        id: id.toString(),
                      })}
                    >
                      <EditOutlined />
                    </Link>
                  )}
                  {canDelete && (
                    <Popconfirm
                      title={intl.formatMessage({
                        defaultMessage:
                          "Are you sure you want to delete this User?",
                      })}
                      onConfirm={() => deleteVenueUser({ variables: { id } })}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]
        : [],
    [canDelete, canEdit, deleteVenueUser, intl, loading]
  );
};

export const useVenueUserList = (): UseVenueUserListReturnType => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy }, setTableQueryParams] =
    useTableQueryParams<VenueUser>();

  const variables = useMemo<VenueUserListVariables>(
    () => ({
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      orderBy,
      filters: {
        ...(venue ? { venueId: { equals: venue?.id } } : undefined),
      },
    }),
    [orderBy, page, pageSize, venue]
  );

  const { data, loading } = useVenueUserListQuery({
    variables,
  });

  const onChange = useCallback<UseVenueUserListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.venueUsers || {};

  return useMemo(
    () => ({
      loading,
      pagination: {
        pageSize,
        current: page,
        total,
      },
      dataSource,
      onChange,
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};
